<template>
  <div>
    <b-row>
      <b-col md="6" class="mb-3">
        <template v-if="attrs.headerInfoComponent">
          <component :is="attrs.headerInfoComponent" v-bind="attrs.headerInfoProps" />
        </template>
      </b-col>
    </b-row>
    <div class="__table-top">
      <h6 v-if="multiple && totalSelected" class="mc-row-check mt-0">
        <span class="badge btn-success">{{ totalSelected }}</span>
        {{ $t('global.object.selectedRow') }}
        <a class="color-primary" @click="$emit('unselectAll')">{{
          $t('global.action.deselect')
        }}</a>
      </h6>

      <McButton
        v-if="attrs.hasStoreAction"
        class="mx-2"
        icon="icon-fill_plus"
        :disabled="isEditing"
        :text="$t('global.addField')"
        @click="$emit('addNewData')"
      />

      <FormulateForm ref="form" v-model="form" class="mc-form">
        <div class="d-flex align-items-center gap-2">
          <FormulateInput
            v-for="filter in attrs.headerFilterComponent"
            :id="filter.name"
            :key="filter.name"
            :type="filter.type"
            :v-model="form[filter.name]"
            :trackBy="filter.trackBy"
            :placeholder="filter.placeholder"
            :optionsGetter="filter.optionsGetter"
            :optionLabelFn="filter.optionLabelFn"
            :fullObject="filter.fullObject"
            v-bind="
              filter.isGrouping
                ? {
                    'group-values': 'libs',
                    'group-label': 'group',
                  }
                : {}
            "
            class="mc-filter-select"
            @input="handleFilterChange"
            @update:fullObject="($event) => (filter.fullObject = $event)"
          />
        </div>
      </FormulateForm>

      <b-input-group size="sm" class="mc-has-search mc-input">
        <template #prepend>
          <i class="mc-icon icon-fill_search"></i>
        </template>

        <b-form-input
          ref="search-input-ref"
          :value="search"
          :disabled="isBusy || isEditing"
          :placeholder="$t('global.search') + '...'"
          @input="handleSearchChange"
        />

        <template v-if="search" #append>
          <button
            class="clear-search-btn"
            :disabled="isBusy || isEditing"
            @click="handleClearSearch"
          >
            <i class="pe-2 mc-icon icon-outline_close"></i>
          </button>
        </template>
      </b-input-group>
      <McButton
        v-if="attrs.hasStoreAction"
        class="mx-2"
        icon="icon-fill_plus"
        :disabled="isEditing"
        :text="$t('global.addField')"
        @click="$emit('addNewData')"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'

export default {
  components: {
    McButton,
  },
  props: {
    attrs: {
      type: Object,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    isBusy: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
    },
    totalSelected: {
      type: Number,
    },
  },
  emits: ['update:search', 'unselectAll', 'addNewData'],
  data() {
    return {
      form: {},
      fullObject: {},
    }
  },
  methods: {
    handleSearchChange: debounce(async function (_search) {
      this.$emit('update:search', _search)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange('')
    },
    handleFilterChange() {
      const baseFilters = this.attrs.listGetterFilter || []

      const filterData = Object.keys(this.form)
        .filter((key) => this.form[key] !== null)
        .map((key) => ({
          id: key.split('select-resourceful-v2_')[1],
          value: this.form[key],
        }))

      const combinedFilters = [
        ...baseFilters,
        ...filterData.map((filter) => `${filter.id} eq '${filter.value}'`),
      ]

      this.$emit('filter-change', combinedFilters)
    },
  },
}
</script>

<style lang="scss" scoped>
.__table-top {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.mc-input {
  background: $white;
  border: 1px solid $teal-200;
  border-radius: 4px;
  line-height: 16px;
  font-family: $font-family-sans-serif;
  color: #344264;
  font-size: 12px;
  width: 100%;
  justify-content: space-between;

  &.input-md {
    height: 37px;
    padding: 10.5px 10px;
  }

  &.input-sm {
    height: 27px;
    padding: 5.5px 10px;
  }

  & > div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    cursor: pointer;
  }

  input {
    padding: 0px;
  }

  input::placeholder {
    color: $teal-200;
    font-size: 12px;
  }

  i {
    margin: -11px;
    margin-left: 0px;
    padding: 8.5px;
    &::before {
      display: block;
      color: #67718b;
      font-size: 20px;
      line-height: none;
    }
  }

  &.__disabled {
    background-color: $gray;
    color: $teal-200;
    cursor: default;

    i::before {
      color: $teal-200;
    }

    input {
      background-color: $gray;
    }
  }
}

.mc-has-search {
  padding: 10.5px;
  padding-left: 10px;
  max-width: 30%;
  margin-left: auto;

  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    border-radius: 4px;
    padding: 0;
    padding-left: 8px;
    font-size: 12px;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon {
      padding-left: 0;
      &:before {
        font-size: 16px;
        color: $input-placeholder-color;
      }
    }
  }
}

.headerInfoComponent {
  margin-bottom: 1rem;
}

.mc-filter-select {
  min-width: 200px;

  :deep(.formulate-input) {
    margin-bottom: 0;
  }
}
</style>
